import { ICurrentUser, IDepartment, IEmployee, IEmployeeDetails, ISpace, ITeam, Role } from '@api'
import { get } from 'lodash'

export enum ROLES {
  ADMIN = 'Admin',
  POWER_ADMIN = 'Power admin',
  DEPT_ADMIN = 'Department admin',
  LOC_ADMIN = 'Location admin',
  IT = 'IT',
  VIEWER = 'Viewer',
  SPACE_CAPTAIN = 'Space Captain',
  BOOKINGS_MANAGER = 'Booking Manager',
}

export const isDepartmentAdmin = (role: Role | null | undefined) => role === Role.DEPARTMENT_ADMIN
export const isLocationAdmin = (role: Role | null | undefined) => role === Role.LOCATION_ADMIN
export const isAdmin = (role: Role | null | undefined) =>
  role === Role.ADMIN || role === Role.POWER_ADMIN
export const isPowerAdmin = (role: Role | null | undefined) => role === Role.POWER_ADMIN
export const isSpaceCaptain = (role: Role | null | undefined) => role === Role.SPACE_CAPTAIN
export const isBookingManager = (role: Role | null | undefined) => role === Role.BOOKINGS_MANAGER
export const isViewer = (role: Role | null | undefined) => role === Role.VIEWER
export const isIT = (role: Role | null | undefined) => role === Role.IT
export const isOnlyAdmin = (role: Role | null | undefined) => role === Role.ADMIN
export const isAnyAdminRole = (role: Role | null | undefined) =>
  role !== Role.VIEWER && role !== Role.IT && role !== Role.BOOKINGS_MANAGER

export const hasDeptManageRights = (
  user: IEmployeeDetails | null | undefined,
  { spaceAssignment: { department: empDept, team: empTeam } }: ISpace
) => {
  const { role, departmentAdmins } = user || { departmentAdmins: [] }
  const empDeptId = empDept?.id
  const empTeamId = empTeam?.id

  return (
    (isDepartmentAdmin(role) || isSpaceCaptain(role)) &&
    departmentAdmins?.some(
      ({ department: { id } }: { department: { id: string } }) =>
        id === empDeptId || id === empTeamId
    )
  )
}

export const hasDeptUserRights = (
  user: IEmployeeDetails,
  { spaceAssignment: { department: empDept, team: empTeam, employee } }: ISpace
) => {
  const { dept, team } = user
  const isAdminAssigned = !!employee && employee.id === user?.id
  const empDeptId = get(empDept, 'id')
  const empTeamId = get(empTeam, 'id')
  const userDeptId = get(dept, 'id')
  const userTeamId = get(team, 'id')

  return (
    isAdminAssigned ||
    empDeptId === userDeptId ||
    (empDeptId === userDeptId && empTeamId === userTeamId)
  )
}

export const isAnyAdmin = (role: Role | undefined) =>
  isAdmin(role) ||
  isPowerAdmin(role) ||
  isSpaceCaptain(role) ||
  isDepartmentAdmin(role) ||
  isLocationAdmin(role)

export const hasLocationManageRights = (
  user: IEmployeeDetails | null | undefined,
  floorId: string
) => {
  const { role, locationAdmins } = user || { locationAdmins: [] }

  return (
    (isLocationAdmin(role) || isSpaceCaptain(role)) &&
    locationAdmins?.some(({ floorPlan: { id } }) => id === floorId)
  )
}

export const hasDeptManageEmpRights = (
  {
    departmentAdmins,
  }: {
    departmentAdmins: {
      department: {
        id: string
      }
    }[]
  },
  shareDept: { id: string }[]
) => {
  return !!departmentAdmins?.filter(({ department: { id } }: { department: { id: string } }) =>
    shareDept?.map((dept: { id: string }) => dept.id).includes(id)
  )
}

export const hasDeptManagableSearchRights = (user: ICurrentUser | null, node: IEmployee) => {
  const role = user?.role
  const departmentAdmins = user?.employee?.departmentAdmins || []
  const isAdmin = isDepartmentAdmin(role) || isSpaceCaptain(role)
  const empDeptId = get(node.dept, 'id')
  const empTeamId = get(node.team, 'id')
  return (
    isAdmin &&
    departmentAdmins.some(({ department: { id } }) => id === empDeptId || id === empTeamId)
  )
}
